<template>
  <div class="custom-pagination">
    <!-- 總數信息 -->
    <span v-if="showTotal" class="pagination-total-text">
      {{ totalText }}
    </span>
    
    <!-- 上一頁按鈕 -->
    <button 
      class="pagination-prev" 
      :class="{ 'pagination-disabled': current === 1 }"
      :disabled="current === 1" 
      @click="handlePrev"
    >
      ‹
    </button>
    
    <!-- 第一頁按鈕（當不在第一頁附近時顯示） -->
    <button
      v-if="showFirstPage"
      class="pagination-item"
      :class="{ 'pagination-item-active': current === 1 }"
      @click="handlePageClick(1)"
    >
      1
    </button>
    
    <!-- 省略號（當需要時顯示） -->
    <span v-if="showFirstEllipsis" class="pagination-ellipsis">...</span>
    
    <!-- 頁碼按鈕 -->
    <button
      v-for="page in displayedPages"
      :key="page"
      class="pagination-item"
      :class="{ 'pagination-item-active': page === current }"
      @click="handlePageClick(page)"
    >
      {{ page }}
    </button>
    
    <!-- 省略號（當需要時顯示） -->
    <span v-if="showLastEllipsis" class="pagination-ellipsis">...</span>
    
    <!-- 最後一頁按鈕（當不在最後一頁附近時顯示） -->
    <button
      v-if="showLastPage"
      class="pagination-item"
      :class="{ 'pagination-item-active': current === totalPages }"
      @click="handlePageClick(totalPages)"
    >
      {{ totalPages }}
    </button>
    
    <!-- 下一頁按鈕 -->
    <button 
      class="pagination-next" 
      :class="{ 'pagination-disabled': current === totalPages || totalPages === 0 }"
      :disabled="current === totalPages || totalPages === 0" 
      @click="handleNext"
    >
      ›
    </button>
    
    <!-- 每頁顯示數量選擇器 -->
    <div v-if="showSizeChanger" class="pagination-options">
      <select class="page-size-selector" :value="pageSize" @change="handlePageSizeChange">
        <option v-for="size in pageSizeOptions" :key="size" :value="parseInt(size)">
          {{ size }} 筆/頁
        </option>
      </select>
    </div>
    
    <!-- 快速跳轉 -->
    <div v-if="showQuickJumper" class="pagination-options quick-jumper">
      <span>跳至</span>
      <input
        type="text"
        v-model="jumpToPage"
        @keyup.enter="handleJumpToPage"
        class="jump-input"
      />
      <span>頁</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  current: {
    type: Number,
    default: 1
  },
  pageSize: {
    type: Number,
    default: 20
  },
  total: {
    type: Number,
    default: 0
  },
  pageSizeOptions: {
    type: Array,
    default: () => ['10', '20', '50', '100']
  },
  showSizeChanger: {
    type: Boolean,
    default: true
  },
  showQuickJumper: {
    type: Boolean,
    default: true
  },
  showTotal: {
    type: [Boolean, Function],
    default: true
  },
  size: {
    type: String,
    default: 'default'
  }
})

const emit = defineEmits(['change', 'show-size-change'])

const jumpToPage = ref('')

// 計算總頁數
const totalPages = computed(() => {
  return Math.ceil(props.total / props.pageSize)
})

// 計算是否顯示第一頁按鈕
const showFirstPage = computed(() => {
  const current = props.current
  const total = totalPages.value
  if (total <= 7) return false // 總頁數少於等於7時不顯示單獨的第一頁按鈕
  return current > 4 // 當前頁大於4時顯示第一頁按鈕
})

// 計算是否顯示第一頁省略號
const showFirstEllipsis = computed(() => {
  const current = props.current
  const total = totalPages.value
  if (total <= 7) return false
  return current > 4 // 當前頁大於4時顯示省略號
})

// 計算是否顯示最後一頁按鈕
const showLastPage = computed(() => {
  const current = props.current
  const total = totalPages.value
  if (total <= 7) return false // 總頁數少於等於7時不顯示單獨的最後一頁按鈕
  return current < total - 3 // 當前頁小於總頁數-3時顯示最後一頁按鈕
})

// 計算是否顯示最後一頁省略號
const showLastEllipsis = computed(() => {
  const current = props.current
  const total = totalPages.value
  if (total <= 7) return false
  return current < total - 3 // 當前頁小於總頁數-3時顯示省略號
})

// 計算要顯示的頁碼數組
const displayedPages = computed(() => {
  const maxPages = 5
  const current = props.current
  const total = totalPages.value
  
  if (total <= 7) {
    // 如果總頁數小於等於7，顯示所有頁碼
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  
  // 計算顯示範圍
  let start, end
  
  if (current <= 4) {
    // 當前頁在前4頁，顯示前5頁
    start = 1
    end = Math.min(5, total)
  } else if (current >= total - 3) {
    // 當前頁在後3頁，顯示後5頁
    start = Math.max(1, total - 4)
    end = total
  } else {
    // 當前頁在中間，顯示當前頁前後各2頁
    start = current - 2
    end = current + 2
  }
  
  const pages = []
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// 計算總數信息文字
const totalText = computed(() => {
  if (typeof props.showTotal === 'function') {
    const start = props.total === 0 ? 0 : (props.current - 1) * props.pageSize + 1
    const end = Math.min(props.current * props.pageSize, props.total)
    return props.showTotal(props.total, [start, end])
  }
  
  if (props.showTotal === true) {
    const start = props.total === 0 ? 0 : (props.current - 1) * props.pageSize + 1
    const end = Math.min(props.current * props.pageSize, props.total)
    return `顯示 ${start}-${end} 筆，共 ${props.total} 筆資料`
  }
  
  return ''
})

// 處理頁碼點擊
const handlePageClick = (page) => {
  if (page === props.current) return
  emit('change', page, props.pageSize)
}

// 處理上一頁
const handlePrev = () => {
  if (props.current > 1) {
    emit('change', props.current - 1, props.pageSize)
  }
}

// 處理下一頁
const handleNext = () => {
  if (props.current < totalPages.value) {
    emit('change', props.current + 1, props.pageSize)
  }
}

// 處理每頁顯示數量變化
const handlePageSizeChange = (event) => {
  const newPageSize = parseInt(event.target.value)
  emit('show-size-change', props.current, newPageSize)
  emit('change', 1, newPageSize) // 改變每頁顯示數量時重置到第一頁
}

// 處理快速跳轉
const handleJumpToPage = () => {
  const page = parseInt(jumpToPage.value)
  if (!isNaN(page) && page >= 1 && page <= totalPages.value) {
    emit('change', page, props.pageSize)
    jumpToPage.value = ''
  }
}

// 監聽 current 變化，清空跳轉輸入框
watch(() => props.current, () => {
  jumpToPage.value = ''
})
</script>

<style scoped>
@import '../assets/styles/CustomPagination.css';
</style>

